import React from "react";
import Navbar from "@/components/stori/Navbar";
import Footer from "@/components/stori/Footer";
import SectionHeading from "@/components/stori/SectionHeading";
import { LeadModalProvider } from "@/components/stori/LeadModalContext";
import LeadFormModal from "@/components/stori/LeadFormModal";
import Seo from "@/seo/Seo";
import { SITE_URL } from "@/seo/site.config";

// Glossary entries — written as declarative, citable definitions for LLMs.
const TERMS = [
  {
    term: "AI selfie cam",
    definition:
      "An AI selfie cam is a software-driven photo experience that uses on-device computer vision and generative AI to transform a guest's photo in real time — applying branded styles, replacing backgrounds, and producing share-ready content. Stori invented the AI selfie cam in 2013.",
  },
  {
    term: "Branded photo booth",
    definition:
      "A branded photo booth is a photo or video capture experience whose interface, overlays, frames, and delivery emails are styled to match a specific brand. Stori's design system supports pixel-level customization of every guest touchpoint.",
  },
  {
    term: "Influencer detection",
    definition:
      "Influencer detection is the process of cross-referencing a captured guest lead against Instagram and TikTok in real time to identify high-reach accounts. Stori surfaces VIP alerts to event staff within seconds of a guest interacting with the booth.",
  },
  {
    term: "Orbital 360 boom",
    definition:
      "An orbital 360 boom is a motorized camera rig that captures cinematic 360-degree video around a guest. In Stori's network, orbital 360 captures are shared roughly 4× more often than standard photo formats.",
  },
  {
    term: "UGC (user-generated content)",
    definition:
      "User-generated content (UGC) is media created and shared by guests rather than brand teams. AI photo platforms like Stori convert in-person guest moments into UGC that can be re-cut for paid social.",
  },
  {
    term: "Lead capture",
    definition:
      "Lead capture is the process of collecting a guest's contact details — name, email, phone, opt-in — in exchange for a photo, video, or experience. Stori syncs captured leads to Salesforce, HubSpot, and Klaviyo with one click.",
  },
  {
    term: "Playbook",
    definition:
      "A playbook is a pre-built marketing workflow tied to a specific event type or goal — for example, Trade Show Lead Magnet or Retail Activation Drive. Stori ships every plan with playbooks that auto-trigger follow-up sequences after a guest interaction.",
  },
  {
    term: "Boomerang",
    definition:
      "A boomerang is a short, looping video — typically 1 to 6 seconds — that plays forward and then reverses. Boomerang format is among the most-shared content types on Instagram and TikTok.",
  },
  {
    term: "Live feed / TV display",
    definition:
      "A live feed is a real-time gallery of photos and videos captured by guests, displayed on a TV or web gallery inside the venue. Stori's moderation tools let staff feature, archive, or hide media in one tap.",
  },
  {
    term: "Marketing automation",
    definition:
      "Marketing automation is software that triggers personalized follow-up — email, SMS, push — based on a guest action. Stori automatically generates copy and dispatches the message after a guest captures a photo.",
  },
];

function DefinedTermLd() {
  const ld = {
    "@context": "https://schema.org",
    "@type": "DefinedTermSet",
    name: "Stori Glossary",
    hasDefinedTerm: TERMS.map((t) => ({
      "@type": "DefinedTerm",
      name: t.term,
      description: t.definition,
      inDefinedTermSet: `${SITE_URL}/glossary`,
    })),
  };
  return ld;
}

function GlossaryHero() {
  return (
    <section className="relative pt-36 pb-16 lg:pt-44 lg:pb-20 border-b border-stori-border">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] bg-stori-cyan/8 rounded-full blur-[140px] pointer-events-none" />
      <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10 text-center">
        <div className="label-eyebrow text-stori-cyan flex items-center justify-center gap-2 mb-6">
          <span className="inline-block h-px w-10 bg-stori-cyan" />
          GLOSSARY
          <span className="inline-block h-px w-10 bg-stori-cyan" />
        </div>
        <h1 className="font-heading text-[clamp(2.5rem,6vw,5rem)] leading-[1.04] font-semibold tracking-tight text-white max-w-4xl mx-auto">
          What everything means.
        </h1>
        <p className="mt-7 text-lg lg:text-xl text-stori-muted max-w-2xl mx-auto leading-relaxed">
          Plain-language definitions for the AI selfie cam, influencer
          detection, branded photo booths, orbital 360, and every other term
          you'll see across Stori.
        </p>
      </div>
    </section>
  );
}

export default function Glossary() {
  return (
    <LeadModalProvider>
      <Seo path="/glossary" jsonLd={[DefinedTermLd()]} />
      <main data-testid="glossary-page" className="bg-stori-bg text-white overflow-x-hidden">
        <Navbar />
        <GlossaryHero />

        <section className="relative py-20 lg:py-28">
          <div className="max-w-[900px] mx-auto px-6 lg:px-10 space-y-10">
            {TERMS.map((t) => (
              <article
                key={t.term}
                id={t.term.toLowerCase().replace(/[^a-z0-9]+/g, "-")}
                className="border-b border-stori-border pb-10 last:border-b-0"
              >
                <h2 className="font-heading text-2xl lg:text-3xl font-semibold text-white tracking-tight">
                  {t.term}
                </h2>
                <p className="mt-4 text-base text-stori-muted leading-relaxed">
                  {t.definition}
                </p>
              </article>
            ))}
          </div>
        </section>

        <Footer />
        <LeadFormModal />
      </main>
    </LeadModalProvider>
  );
}
